import moment from 'moment';
import { quoteShiftInEventZone } from './quoteEventTime';

/** startDate/endDate are calendar fields, even when serialized as ISO dates. */
export const formatQuoteCalendarDate = (value: unknown): string => {
  if (value === null || value === undefined) return 'N/A';
  if (value === '') return '';
  const date = moment.utc(String(value).split('T')[0], 'YYYY-MM-DD', true);
  return date.isValid() ? date.format('MM/DD/YYYY') : String(value);
};

export const clientClockWallValue = (clock: string, eventTimezone?: string): string => {
  const canonical = parseClientClock(clock || '');
  if (canonical !== null) return canonical;
  if (!clock?.includes('T')) return clock || '';
  const projected = quoteShiftInEventZone({ startsAt: clock } as Record<string, any>, eventTimezone);
  return projected.startTime || moment.parseZone(clock).format('HH:mm');
};

export const formatClientClock = (clock: string, eventTimezone?: string) =>
  clock ? moment(parseClientClock(clock) ?? clientClockWallValue(clock, eventTimezone), 'HH:mm', true).format('h:mm A') : '';

/** Legacy comparison payloads contain either one clock or a wall-clock range. */
export const formatClientClockValue = (value: string): string => {
  const range = /^(\d{1,2}:\d{2}(?:\s*[AP]M)?)\s*[-–]\s*(\d{1,2}:\d{2}(?:\s*[AP]M)?)$/i.exec(value.trim());
  if (range) return `${formatClientClock(range[1])}-${formatClientClock(range[2])}`;
  const formatted = formatClientClock(value);
  return formatted && formatted !== 'Invalid date' ? formatted : value;
};

export const parseClientClock = (draft: string): string | null => {
  const match = /^(1[0-2]|0?[1-9]):([0-5]\d)\s*(AM|PM)$/i.exec(draft.trim());
  if (!match) return null;
  const hour = Number(match[1]) % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
  return `${String(hour).padStart(2, '0')}:${match[2]}`;
};

/** Short display label for an event zone ("America/New_York" -> "EDT"); stored IANA values stay untouched. */
export const shortZoneLabel = (eventTimezone?: string, onDate?: unknown): string => {
  if (!eventTimezone || !eventTimezone.includes('/')) return eventTimezone || '';
  try {
    return new Intl.DateTimeFormat('en-US', { timeZone: eventTimezone, timeZoneName: 'short' }).formatToParts(onDate && !Number.isNaN(new Date(String(onDate)).getTime()) ? new Date(String(onDate)) : new Date()).find(part => part.type === 'timeZoneName')?.value || eventTimezone;
  } catch {
    return eventTimezone;
  }
};

const dateOnly = (value: unknown) => String(value || '').split('T')[0];

export const shiftEndsNextDay = (shift: { startDate?: unknown; endDate?: unknown; startTime?: unknown; endTime?: unknown }) => {
  const startDate = dateOnly(shift.startDate);
  const endDate = dateOnly(shift.endDate);
  if (startDate && endDate && startDate !== endDate) return endDate > startDate;
  return Boolean(shift.startTime && shift.endTime && String(shift.endTime) < String(shift.startTime));
};

export const formatShiftTimeRange = (shift: { startDate?: unknown; endDate?: unknown; startTime?: unknown; endTime?: unknown }, eventTimezone?: string) => {
  if (!shift.startTime || !shift.endTime) return '';
  const start = formatClientClock(String(shift.startTime), eventTimezone);
  const end = formatClientClock(String(shift.endTime), eventTimezone);
  const wallShift = { ...shift, startTime: clientClockWallValue(String(shift.startTime), eventTimezone), endTime: clientClockWallValue(String(shift.endTime), eventTimezone) };
  return `${start} - ${end}${shiftEndsNextDay(wallShift) ? ' (+1 day)' : ''}${eventTimezone ? ` · ${shortZoneLabel(eventTimezone, dateOnly(shift.startDate) || undefined)}` : ''}`;
};
